import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, registerUrl, runningPath } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import { NAMSL_BALTIMORE_TUSKERS_RECAP_SLUG } from '../config/namslBaltimoreTuskersRecapPost';
import { compressImageForUpload, isImageFile } from '../utils/imageUpload';
import { normalizeClubSport } from '../config/clubSports';
import './ClubAdminDashboard.css';
import './PracticePlanSection.css';

const formatCoordinatorRole = (role) => {
  if (role === 'MATCH_COORDINATOR') return 'Match Coordinator';
  if (role === 'PRACTICE_COORDINATOR') return 'Practice Coordinator';
  if (role === 'PRACTICE_SPECIALIST') return 'Practice Specialist';
  return role?.replace(/_/g, ' ') || '';
};

const ClubAdminDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registrationLink, setRegistrationLink] = useState('');
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [playerFormData, setPlayerFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    position: 'MIDFIELDER',
    skillLevel: 5,
    skillCategory: '',
  });
  const [addToClub, setAddToClub] = useState(true);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const [ratingModalPlayer, setRatingModalPlayer] = useState(null);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [ratingSummaryLoading, setRatingSummaryLoading] = useState(false);
  const [ratingsExportLoading, setRatingsExportLoading] = useState(false);
  const [landingFormData, setLandingFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    imageUrl: '',
  });
  const [uploadingClubImage, setUploadingClubImage] = useState('');
  const [selectedSpecialistId, setSelectedSpecialistId] = useState('');
  const [statusBlogPublishing, setStatusBlogPublishing] = useState(false);

  const loadClub = useCallback(async () => {
    try {
      const data = await api.getClubById(id);
      if (normalizeClubSport(data.sport) === 'RUNNING') {
        navigate(runningPath(`/clubs/${id}/admin`), { replace: true });
        return;
      }
      setClub(data);
      setLandingFormData({
        name: data.name || '',
        description: data.description || '',
        logoUrl: data.logoUrl || '',
        imageUrl: data.imageUrl || '',
      });
      setRegistrationLink(registerUrl(id));
    } catch (err) {
      setError(err.message || 'Failed to load club');
    }
  }, [id, navigate]);

  const loadMembers = useCallback(async () => {
    try {
      const allMembers = await api.getClubMembers(id, null, user?.id);
      setMembers(allMembers.filter(m => m.status === 'APPROVED'));
      setPendingMembers(allMembers.filter(m => m.status === 'PENDING'));
    } catch (err) {
      setError(err.message || 'Failed to load members');
    }
  }, [id, user?.id]);

  const currentPracticeSpecialist = members.find((m) =>
    m.roles?.includes('PRACTICE_SPECIALIST')
  );

  useEffect(() => {
    if (currentPracticeSpecialist?.playerId) {
      setSelectedSpecialistId(currentPracticeSpecialist.playerId);
    } else if (members.length > 0 && !selectedSpecialistId) {
      setSelectedSpecialistId(members[0].playerId);
    }
  }, [currentPracticeSpecialist, members, selectedSpecialistId]);

  const loadPlayers = useCallback(async () => {
    try {
      const data = await api.getClubPlayers(id, user?.id);
      setPlayers(data);
    } catch (err) {
      setError(err.message || 'Failed to load players');
    }
  }, [id, user]);

  useEffect(() => {
    loadClub();
    loadMembers();
    if (activeTab === 'players') {
      loadPlayers();
    }
  }, [id, activeTab, loadClub, loadMembers, loadPlayers]);

  // Helper function to properly encode message for WhatsApp with emoji support
  const encodeWhatsAppMessage = (message) => {
    return encodeURIComponent(message);
  };

  // Get WhatsApp URL - use api.whatsapp.com for better emoji support
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeWhatsAppMessage(message);
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  };

  const handleShareViaWhatsApp = () => {
    const link = registrationLink || registerUrl(id);
    if (!link || !club) {
      setError('Registration link is not available');
      return;
    }

    const message = `⚽ *${club.name} - Join our club*\n\n` +
      `Sign up in one step (account + player profile):\n\n` +
      `${link}\n\n` +
      (club.description ? `About: ${club.description}\n` : '');

    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const handlePublishStatusBlog = async () => {
    if (!user?.id) return;
    setStatusBlogPublishing(true);
    setError('');
    setSuccess('');
    try {
      const post = await api.publishClubStatusBlog(id, user.id);
      setSuccess(`Live status blog updated: "${post.title}"`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to publish status blog');
    } finally {
      setStatusBlogPublishing(false);
    }
  };

  const handleToggleAllowNewMembers = async () => {
    if (!club) return;
    
    const newValue = !club.allowNewMembers;
    try {
      setLoading(true);
      const updatedClub = await api.updateClubSettings(id, newValue, user.id);
      setClub(updatedClub);
      setSuccess(`New member requests ${newValue ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update club settings');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleLandingFormChange = (e) => {
    const { name, value } = e.target;
    setLandingFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setSuccess('');
  };

  const handleSaveLandingPage = async (e) => {
    e.preventDefault();
    if (!club || !user?.id) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const updatedClub = await api.updateClub(id, {
        name: landingFormData.name.trim(),
        description: landingFormData.description.trim(),
        logoUrl: landingFormData.logoUrl.trim() || null,
        imageUrl: landingFormData.imageUrl.trim() || null,
      }, user.id);
      setClub(updatedClub);
      setLandingFormData({
        name: updatedClub.name || '',
        description: updatedClub.description || '',
        logoUrl: updatedClub.logoUrl || '',
        imageUrl: updatedClub.imageUrl || '',
      });
      setSuccess('Landing page updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update landing page');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleClubImageUpload = async (imageType, file) => {
    if (!file || !user?.id) return;

    try {
      setUploadingClubImage(imageType);
      setError('');
      setSuccess('');
      const updatedClub = await api.uploadClubImage(id, imageType, file, user.id);
      setClub(updatedClub);
      setLandingFormData({
        name: updatedClub.name || '',
        description: updatedClub.description || '',
        logoUrl: updatedClub.logoUrl || '',
        imageUrl: updatedClub.imageUrl || '',
      });
      setSuccess(`${imageType === 'logo' ? 'Logo' : 'Landing image'} uploaded successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
      setTimeout(() => setError(''), 5000);
    } finally {
      setUploadingClubImage('');
    }
  };

  const handleApprovePlayer = async (playerId) => {
    try {
      setLoading(true);
      await api.approvePlayer(id, playerId, user.id);
      setSuccess('Player approved successfully');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to approve player');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPlayer = async (playerId) => {
    try {
      setLoading(true);
      await api.rejectPlayer(id, playerId, user.id);
      setSuccess('Player rejected');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reject player');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (playerId, role) => {
    try {
      setLoading(true);
      await api.assignCoordinatorRole(id, playerId, role, user.id);
      setSuccess('Role assigned successfully');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to assign role');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPracticeSpecialist = async () => {
    if (!selectedSpecialistId) {
      setError('Select a member to assign as practice specialist');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.setPracticeSpecialist(id, selectedSpecialistId, user.id);
      setSuccess('Practice specialist updated');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to assign practice specialist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePracticeSpecialist = async () => {
    if (!window.confirm('Remove the current practice specialist?')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.removePracticeSpecialist(id, user.id);
      setSuccess('Practice specialist removed');
      setSelectedSpecialistId(members[0]?.playerId || '');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to remove practice specialist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (playerId, role, playerName) => {
    const label = formatCoordinatorRole(role);
    if (!window.confirm(`Remove ${label} from ${playerName || 'this member'}?`)) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await api.removeCoordinatorRole(id, playerId, role, user.id);
      setSuccess(`${label} role removed`);
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to remove role');
    } finally {
      setLoading(false);
    }
  };

  const openRatingModal = async (player) => {
    setRatingModalPlayer(player);
    setRatingSummary(null);
    setRatingSummaryLoading(true);
    try {
      const data = await api.getPlayerRatingSummary(id, player.id, user.id);
      setRatingSummary(data);
    } catch (err) {
      setRatingSummary({ error: err.message || 'Failed to load ratings' });
    } finally {
      setRatingSummaryLoading(false);
    }
  };

  const downloadRatingsJsonFile = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (club?.name || 'club').trim().replace(/\s+/g, '-').toLowerCase();
    link.href = url;
    link.download = `${safeName}-players-ratings.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const buildClientRatingsExport = async () => {
    const summaries = await Promise.all(
      players.map((player) =>
        api.getPlayerRatingSummary(id, player.id, user.id).catch(() => ({ count: 0 }))
      )
    );
    const exportPlayers = players
      .map((player, index) => {
        const summary = summaries[index] || { count: 0 };
        return {
          name: player.name,
          position: player.position,
          skillLevel: player.skillLevel,
          skillCategory: player.skillCategory,
          peerRatingAverage: summary.count > 0 ? summary.averageRating : null,
          peerRatingCount: summary.count || 0,
        };
      })
      .sort((a, b) => {
        const skillDiff = (b.skillLevel ?? 0) - (a.skillLevel ?? 0);
        if (skillDiff !== 0) return skillDiff;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });

    return {
      clubId: id,
      clubName: club?.name,
      exportedAt: new Date().toISOString(),
      players: exportPlayers,
    };
  };

  const handleDownloadRatingsExport = async () => {
    if (!user?.id) return;
    setRatingsExportLoading(true);
    setError('');
    try {
      let data;
      try {
        data = await api.exportClubPlayerRatings(id, user.id);
      } catch (apiErr) {
        if (players.length === 0) {
          throw apiErr;
        }
        data = await buildClientRatingsExport();
      }
      downloadRatingsJsonFile(data);
      setSuccess('Player ratings JSON downloaded.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to export player ratings');
    } finally {
      setRatingsExportLoading(false);
    }
  };

  const handlePlayerFormChange = (e) => {
    const { name, value } = e.target;
    setPlayerFormData((prev) => ({
      ...prev,
      [name]: name === 'skillLevel' ? parseInt(value, 10) : value,
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isImageFile(file)) {
      setError('Please select an image file (JPG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be 10 MB or smaller.');
      return;
    }
    setProfilePictureFile(file);
    setProfilePreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const resetPlayerForm = () => {
    setPlayerFormData({
      name: '',
      email: '',
      phoneNumber: '',
      dateOfBirth: '',
      position: 'MIDFIELDER',
      skillLevel: 5,
      skillCategory: '',
    });
    setProfilePictureFile(null);
    if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    setProfilePreviewUrl(null);
    setAddToClub(true);
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let profilePictureUrl = null;
      if (profilePictureFile) {
        const uploadFile = await compressImageForUpload(profilePictureFile);
        profilePictureUrl = await api.uploadPlayerProfilePicture(uploadFile);
      }
      await api.addClubPlayer(id, user.id, {
        ...playerFormData,
        dateOfBirth: playerFormData.dateOfBirth || null,
        skillCategory: playerFormData.skillCategory || null,
        ...(profilePictureUrl && { profilePictureUrl }),
      }, addToClub);
      setSuccess('Player added successfully');
      resetPlayerForm();
      setShowPlayerForm(false);
      loadPlayers();
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlayer = (player) => {
    navigate(soccerPath(`/clubs/${id}/players/${player.id}/edit`));
  };

  const handleDeletePlayer = async (playerId, removeFromClubOnly = false) => {
    if (!window.confirm(`Are you sure you want to ${removeFromClubOnly ? 'remove this player from the club' : 'delete this player'}?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.deleteClubPlayer(id, playerId, user.id, removeFromClubOnly);
      setSuccess(removeFromClubOnly ? 'Player removed from club successfully' : 'Player deleted successfully');
      loadPlayers();
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete player');
    } finally {
      setLoading(false);
    }
  };

  const cancelPlayerForm = () => {
    setShowPlayerForm(false);
    resetPlayerForm();
  };

  if (!club) {
    return <div className="loading">Loading...</div>;
  }

  const isClubAdminUser = user && club.adminUserId === user.id;
  if (!isClubAdminUser && !isAdmin()) {
    return (
      <div className="club-admin-dashboard">
        <div className="alert alert-error">
          You do not have permission to access the club admin dashboard.
        </div>
        <Link to={soccerPath(`/clubs/${id}`)} className="btn btn-secondary">
          Back to club
        </Link>
      </div>
    );
  }

  return (
    <div className="club-admin-dashboard">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(soccerPath(`/clubs/${id}`))}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
        >
          ← Back to Club
        </button>
        <h2 style={{ flex: '1', minWidth: '200px' }}>Club Admin Dashboard - {club.name}</h2>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="admin-tabs">
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={activeTab === 'members' ? 'active' : ''}
          onClick={() => setActiveTab('members')}
        >
          Members ({members.length})
        </button>
        <button
          className={activeTab === 'pending' ? 'active' : ''}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pendingMembers.length})
        </button>
        <button
          className={activeTab === 'players' ? 'active' : ''}
          onClick={() => setActiveTab('players')}
        >
          Players ({players.length})
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="admin-content">
          <h3>Landing Page</h3>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
            Customize what players and visitors see on the club home page.
          </p>
          <form className="club-landing-settings" onSubmit={handleSaveLandingPage}>
            <div className="form-group">
              <label htmlFor="landing-name">Club name</label>
              <input
                id="landing-name"
                name="name"
                type="text"
                value={landingFormData.name}
                onChange={handleLandingFormChange}
                required
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="landing-description">Club description</label>
              <textarea
                id="landing-description"
                name="description"
                value={landingFormData.description}
                onChange={handleLandingFormChange}
                maxLength={500}
                rows={4}
                placeholder="Tell players and visitors about your club"
              />
            </div>

            <div className="club-landing-settings__media-grid">
              <div className="club-landing-settings__media-card">
                <label htmlFor="landing-logo">Club logo URL</label>
                <input
                  id="landing-logo"
                  name="logoUrl"
                  type="url"
                  value={landingFormData.logoUrl}
                  onChange={handleLandingFormChange}
                  placeholder="https://..."
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleClubImageUpload('logo', e.target.files?.[0])}
                  disabled={uploadingClubImage === 'logo'}
                />
                {landingFormData.logoUrl && (
                  <img
                    src={mediaUrl(landingFormData.logoUrl)}
                    alt="Club logo preview"
                    className="club-landing-settings__logo-preview"
                  />
                )}
                {uploadingClubImage === 'logo' && <p className="form-help">Uploading logo...</p>}
              </div>

              <div className="club-landing-settings__media-card">
                <label htmlFor="landing-image">Landing page image URL</label>
                <input
                  id="landing-image"
                  name="imageUrl"
                  type="url"
                  value={landingFormData.imageUrl}
                  onChange={handleLandingFormChange}
                  placeholder="https://..."
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleClubImageUpload('landing', e.target.files?.[0])}
                  disabled={uploadingClubImage === 'landing'}
                />
                {landingFormData.imageUrl && (
                  <img
                    src={mediaUrl(landingFormData.imageUrl)}
                    alt="Landing page preview"
                    className="club-landing-settings__image-preview"
                  />
                )}
                {uploadingClubImage === 'landing' && <p className="form-help">Uploading landing image...</p>}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || Boolean(uploadingClubImage)}>
                {loading ? 'Saving...' : 'Save landing page'}
              </button>
              <Link to={soccerPath(`/clubs/${id}`)} className="btn btn-secondary">
                Preview club page
              </Link>
            </div>
          </form>

          <h3 style={{ marginTop: '2rem' }}>Player signup link</h3>
          <p className="form-help" style={{ marginBottom: '0.75rem' }}>
            Share this link so new players can create an account, set up their profile, and request
            to join {club?.name || 'your club'} in one step.
          </p>
          <div className="registration-link-section">
            <input
              type="text"
              value={registrationLink || registerUrl(id)}
              readOnly
              className="link-input"
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(registrationLink || registerUrl(id));
                  setSuccess('Link copied to clipboard!');
                  setTimeout(() => setSuccess(''), 2000);
                }}
                className="btn btn-secondary"
              >
                Copy Link
              </button>
              <button
                type="button"
                onClick={handleShareViaWhatsApp}
                className="btn btn-primary"
                style={{ backgroundColor: '#25D366' }}
              >
                📱 Share with WhatsApp
              </button>
            </div>
          </div>
          
          <h3 style={{ marginTop: '2rem' }}>Tournaments</h3>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
            Update live tournament scores and player statistics, then publish a public page for fans to follow along.
          </p>
          <Link to={soccerPath(`/clubs/${id}/admin/tournaments`)} className="btn btn-primary">
            Manage tournaments
          </Link>

          <h3 style={{ marginTop: '2rem' }}>Club blog</h3>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
            Share news, tournament wins, and photos with the community on the public blog.
            A live schedule &amp; results post auto-updates every 2 hours from tournament and match data.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <Link to={soccerPath(`/clubs/${id}/admin/blog`)} className="btn btn-primary">
              Manage blog posts
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePublishStatusBlog}
              disabled={statusBlogPublishing}
            >
              {statusBlogPublishing ? 'Publishing…' : 'Publish live status now'}
            </button>
            <a
              href="/blog/columbus-tuskers-live-status"
              className="btn btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              View live status blog
            </a>
            <a
              href={`/blog/${NAMSL_BALTIMORE_TUSKERS_RECAP_SLUG}`}
              className="btn btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              View NAMSL Baltimore recap
            </a>
          </div>

          <h3 style={{ marginTop: '2rem' }}>Practice specialist</h3>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
            Assign one club member to post practice guidance (warm-up, drills, session goals) for upcoming practices.
          </p>
          <div className="practice-plan-specialist-settings">
            <p className="practice-plan-specialist-settings__current">
              <strong>Current specialist:</strong>{' '}
              {currentPracticeSpecialist?.player?.name || 'None assigned'}
            </p>
            {members.length === 0 ? (
              <p style={{ color: '#666' }}>Approve members before assigning a practice specialist.</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={selectedSpecialistId}
                    onChange={(e) => setSelectedSpecialistId(e.target.value)}
                    disabled={loading}
                    style={{ minWidth: '220px', padding: '0.5rem' }}
                  >
                    {members.map((member) => (
                      <option key={member.playerId} value={member.playerId}>
                        {member.player?.name || 'Unknown'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSetPracticeSpecialist}
                    disabled={loading || !selectedSpecialistId}
                  >
                    {currentPracticeSpecialist ? 'Change specialist' : 'Assign specialist'}
                  </button>
                  {currentPracticeSpecialist && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleRemovePracticeSpecialist}
                      disabled={loading}
                    >
                      Remove specialist
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <h3 style={{ marginTop: '2rem' }}>Club Settings</h3>
          <div className="club-settings-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={club?.allowNewMembers !== false}
                  onChange={handleToggleAllowNewMembers}
                  disabled={loading || !club}
                />
                <span>Allow new member requests</span>
              </label>
            </div>
            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
              {club?.allowNewMembers !== false 
                ? 'Players can request to join this club' 
                : 'New member requests are disabled. Existing pending requests can still be processed.'}
            </p>
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div className="admin-content">
          <h3>Approved Members</h3>
          {members.length === 0 ? (
            <p>No approved members yet.</p>
          ) : (
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-card">
                  <h4>{member.player?.name || 'Unknown'}</h4>
                  <p><strong>Email:</strong> {member.player?.email || 'N/A'}</p>
                  <p><strong>Position:</strong> {member.player?.position || 'N/A'}</p>
                  {member.roles?.length > 0 && (
                    <div className="member-card__roles">
                      {member.roles.map((role) => (
                        <span key={role} className="member-card__role-badge">
                          {formatCoordinatorRole(role)}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="member-actions" style={{ flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Link
                      to={soccerPath(`/clubs/${id}/players/${member.playerId}`)}
                      className="btn btn-sm btn-secondary"
                    >
                      View Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleEditPlayer({ id: member.playerId })}
                      className="btn btn-sm btn-primary"
                      disabled={loading}
                    >
                      Edit Profile
                    </button>
                  </div>
                  <div className="member-actions member-actions--roles">
                    {member.roles?.includes('MATCH_COORDINATOR') ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveRole(
                            member.playerId,
                            'MATCH_COORDINATOR',
                            member.player?.name
                          )
                        }
                        className="btn btn-sm btn-danger"
                        disabled={loading}
                      >
                        Revoke Match Coordinator
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAssignRole(member.playerId, 'MATCH_COORDINATOR')}
                        className="btn btn-sm btn-secondary"
                        disabled={loading}
                      >
                        Assign Match Coordinator
                      </button>
                    )}
                    {member.roles?.includes('PRACTICE_COORDINATOR') ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveRole(
                            member.playerId,
                            'PRACTICE_COORDINATOR',
                            member.player?.name
                          )
                        }
                        className="btn btn-sm btn-danger"
                        disabled={loading}
                      >
                        Revoke Practice Coordinator
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAssignRole(member.playerId, 'PRACTICE_COORDINATOR')}
                        className="btn btn-sm btn-secondary"
                        disabled={loading}
                      >
                        Assign Practice Coordinator
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="admin-content">
          <h3>Pending Approvals</h3>
          {pendingMembers.length === 0 ? (
            <p>No pending approvals.</p>
          ) : (
            <div className="members-list">
              {pendingMembers.map((member) => (
                <div key={member.id} className="member-card">
                  <h4>{member.player?.name || 'Unknown'}</h4>
                  <p><strong>Email:</strong> {member.player?.email || 'N/A'}</p>
                  <p><strong>Position:</strong> {member.player?.position || 'N/A'}</p>
                  <div className="member-actions">
                    <button
                      onClick={() => handleApprovePlayer(member.playerId)}
                      className="btn btn-sm btn-primary"
                      disabled={loading}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectPlayer(member.playerId)}
                      className="btn btn-sm btn-danger"
                      disabled={loading}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'players' && (
        <div className="admin-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h3>Club Players</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDownloadRatingsExport}
                className="btn btn-secondary"
                disabled={ratingsExportLoading || players.length === 0}
                title="Download names and ratings only — no email or phone"
              >
                {ratingsExportLoading ? 'Exporting…' : 'Download JSON'}
              </button>
              <button
                onClick={() => {
                  resetPlayerForm();
                  setShowPlayerForm(true);
                }}
                className="btn btn-primary"
              >
                Add Player
              </button>
            </div>
          </div>

          {showPlayerForm && (
            <div className="card club-player-form" style={{ marginBottom: '2rem' }}>
              <h4>Add New Player</h4>
              <form onSubmit={handleAddPlayer}>
                <div className="club-player-form__photo">
                  <label>Profile photo (optional)</label>
                  <div className="club-player-form__photo-row">
                    {profilePreviewUrl ? (
                      <img src={profilePreviewUrl} alt="Preview" className="club-player-form__avatar" />
                    ) : (
                      <div className="club-player-form__avatar-placeholder">+</div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleProfilePictureChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={playerFormData.name}
                    onChange={handlePlayerFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={playerFormData.email}
                    onChange={handlePlayerFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={playerFormData.phoneNumber}
                    onChange={handlePlayerFormChange}
                  />
                </div>
                <div className="form-group">
                  <label>Date of birth (optional)</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={playerFormData.dateOfBirth}
                    onChange={handlePlayerFormChange}
                  />
                </div>
                <div className="form-group">
                  <label>Position *</label>
                  <select
                    name="position"
                    value={playerFormData.position}
                    onChange={handlePlayerFormChange}
                    required
                  >
                    <option value="GOALKEEPER">Goalkeeper</option>
                    <option value="DEFENDER">Defender</option>
                    <option value="MIDFIELDER">Midfielder</option>
                    <option value="FORWARD">Forward</option>
                    <option value="WINGER">Winger</option>
                    <option value="FLEXIBLE">Flexible</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Skill Level * (1-10): {playerFormData.skillLevel}</label>
                  <input
                    type="range"
                    name="skillLevel"
                    min="1"
                    max="10"
                    value={playerFormData.skillLevel}
                    onChange={handlePlayerFormChange}
                    required
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', color: '#666' }}>
                    <span>1 (Beginner)</span>
                    <span>10 (Expert)</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Experience level (optional)</label>
                  <select
                    name="skillCategory"
                    value={playerFormData.skillCategory}
                    onChange={handlePlayerFormChange}
                    style={{ width: '100%', padding: '0.5rem' }}
                  >
                    <option value="">— Select —</option>
                    <option value="BEGINNER">Beginner</option>
                    <option value="INTERMEDIATE">Intermediate</option>
                    <option value="EXPERT">Expert</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={addToClub}
                      onChange={(e) => setAddToClub(e.target.checked)}
                    />
                    <span>Automatically add to club</span>
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Add Player'}
                  </button>
                  <button type="button" onClick={cancelPlayerForm} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {players.length === 0 ? (
            <p>No players in this club yet.</p>
          ) : (
            <div className="members-list">
              {players.map((player) => {
                const photoUrl = player.profilePictureUrl ? mediaUrl(player.profilePictureUrl) : null;
                return (
                <div key={player.id} className="member-card club-admin-player-card">
                  <div className="club-admin-player-card__header">
                    {photoUrl ? (
                      <img src={photoUrl} alt={player.name} className="club-admin-player-card__avatar" />
                    ) : (
                      <div className="club-admin-player-card__avatar-placeholder">
                        {player.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <h4>{player.name}</h4>
                      <p><strong>Email:</strong> {player.email}</p>
                      {player.phoneNumber && <p><strong>Phone:</strong> {player.phoneNumber}</p>}
                      <p><strong>Position:</strong> {player.position?.replace('_', ' ') || 'N/A'}</p>
                      <p><strong>Skill Level:</strong> {player.skillLevel}/10</p>
                    </div>
                  </div>
                  <div className="member-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <Link
                      to={soccerPath(`/clubs/${id}/players/${player.id}`)}
                      className="btn btn-sm btn-secondary"
                    >
                      View Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => openRatingModal(player)}
                      className="btn btn-sm btn-secondary"
                      disabled={loading}
                    >
                      View ratings
                    </button>
                    <button
                      onClick={() => handleEditPlayer(player)}
                      className="btn btn-sm btn-primary"
                      disabled={loading}
                    >
                      Edit Profile
                    </button>
                    <button
                      onClick={() => handleDeletePlayer(player.id, true)}
                      className="btn btn-sm btn-warning"
                      disabled={loading}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Remove from Club
                    </button>
                    <button
                      onClick={() => handleDeletePlayer(player.id, false)}
                      className="btn btn-sm btn-danger"
                      disabled={loading}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Delete Player
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {ratingModalPlayer && (
            <div className="modal-overlay" onClick={() => setRatingModalPlayer(null)} role="dialog" aria-modal="true" aria-labelledby="rating-modal-title">
              <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                <h3 id="rating-modal-title">Ratings for {ratingModalPlayer.name}</h3>
                {ratingSummaryLoading && <p>Loading…</p>}
                {ratingSummary?.error && <div className="alert alert-error">{ratingSummary.error}</div>}
                {!ratingSummaryLoading && ratingSummary && !ratingSummary.error && (
                  <>
                    <p><strong>Average:</strong> {ratingSummary.averageRating != null ? ratingSummary.averageRating.toFixed(1) : '—'} / 5 ({ratingSummary.count} rating{ratingSummary.count !== 1 ? 's' : ''})</p>
                    {ratingSummary.ratings && ratingSummary.ratings.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0, marginTop: '0.75rem' }}>
                        {ratingSummary.ratings.map((r, i) => (
                          <li key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>
                            {r.rating} star{r.rating !== 1 ? 's' : ''} — {r.ratedAt ? new Date(r.ratedAt).toLocaleDateString(undefined, { dateStyle: 'short' }) : ''}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ color: '#666' }}>No ratings yet.</p>
                    )}
                  </>
                )}
                <button type="button" className="btn btn-secondary" style={{ marginTop: '1rem' }} onClick={() => setRatingModalPlayer(null)}>Close</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClubAdminDashboard;

